import React from 'react';
import { Box, Typography, Avatar, Chip, Divider, Alert, Button } from '@mui/material';
import { AutoAwesome, Email, Phone, LocationOn, LinkedIn, GitHub, Schedule, School, LocalLibrary as Library } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { CircularScore, ScoreBar, SeniorityBar } from './ScoreWidgets';

const CandidatDetail = ({ candidat, jobUuid, selectedMission }) => {
    const history = useHistory();

    const p = candidat.properties || {};
    const indScores = candidat.individual_scores || null;
    const scoreGlobal = Math.round((candidat.score ?? 0) * 100);

    const fullName = (p.full_name && p.full_name !== 'null') ? p.full_name : '';
    const nameParts = fullName.trim().split(' ');
    const initiales = ((nameParts[0]?.charAt(0) || '') + (nameParts[1]?.charAt(0) || '')).toUpperCase() || '?';
    const techSkills = Array.isArray(p.technical_skills) ? p.technical_skills : [];
    const progLangs = Array.isArray(p.programming_languages) ? p.programming_languages : [];
    const spokenLangs = Array.isArray(p.spoken_languages) ? p.spoken_languages : [];
    const rolesHeld = Array.isArray(p.roles_held) ? p.roles_held : [];
    const seniorityTech = Array.isArray(p.seniority_technologies) ? p.seniority_technologies : [];
    const seniorityProgLangs = Array.isArray(p.seniority_programming_languages) ? p.seniority_programming_languages : [];
    const certifs = Array.isArray(p.certifications) ? p.certifications : [];
    const industries = Array.isArray(p.industry_primary_industries) ? p.industry_primary_industries : [];
    const parsingConf = p.parsing_confidence != null ? Math.round(p.parsing_confidence * 100) : null;

    return (
        <Box sx={{ p: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e9ecef' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Avatar sx={{ width: 60, height: 60, bgcolor: '#eef2f5', color: '#20c997', fontSize: '1.5rem', fontWeight: 'bold' }}>
                        {initiales}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', lineHeight: 1.2 }}>
                            {fullName || <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>Nom non disponible</span>}
                        </Typography>
                        {rolesHeld.length > 0 && (
                            <Typography variant="body2" sx={{ color: '#495057' }}>
                                {rolesHeld[0]}
                            </Typography>
                        )}
                        <Typography variant="caption" sx={{ color: '#adb5bd' }}>
                            {[
                                p.location && p.location !== 'not found' ? p.location : null,
                                p.education_level
                            ].filter(Boolean).join(' • ')}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularScore score={scoreGlobal} />
                    <Typography variant="caption" display="block" sx={{ color: '#adb5bd', mt: 0.5, fontSize: '0.6rem', textTransform: 'uppercase' }}>
                        score global
                    </Typography>
                    {parsingConf != null && (
                        <Chip
                            label={`Parsing ${parsingConf}%`}
                            size="small"
                            sx={{
                                mt: 0.5, height: 18, fontSize: '0.6rem',
                                backgroundColor: parsingConf >= 80 ? '#d1f5ea' : parsingConf >= 50 ? '#fff3cd' : '#fde8e8',
                                color: parsingConf >= 80 ? '#0d6f4f' : parsingConf >= 50 ? '#856404' : '#b02a37',
                                border: 'none'
                            }}
                        />
                    )}
                </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<AutoAwesome />}
                    onClick={() => {
                        history.push({
                            pathname: '/candidats-offre/entretien',
                            state: { candidat, jobUuid, selectedMission }
                        });
                    }}
                    disabled={!jobUuid}
                    sx={{
                        backgroundColor: '#6f42c1',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        borderRadius: 2,
                        px: 3,
                        boxShadow: '0 4px 10px rgba(111, 66, 193, 0.2)',
                        '&:hover': { backgroundColor: '#5936a2' }
                    }}
                >
                    Générer un entretien
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {p.email && p.email !== 'not found' && (
                    <Chip icon={<Email fontSize="small" />} label={p.email} size="small" variant="outlined" sx={{ color: '#495057', borderColor: '#dee2e6', fontSize: '0.72rem' }} />
                )}
                {p.phone && p.phone !== 'not found' && (
                    <Chip icon={<Phone fontSize="small" />} label={p.phone} size="small" variant="outlined" sx={{ color: '#495057', borderColor: '#dee2e6', fontSize: '0.72rem' }} />
                )}
                {p.location && p.location !== 'not found' && (
                    <Chip icon={<LocationOn fontSize="small" />} label={p.location} size="small" variant="outlined" sx={{ color: '#495057', borderColor: '#dee2e6', fontSize: '0.72rem' }} />
                )}
                {p.linkedin && p.linkedin !== 'not found' && (
                    <Chip
                        icon={<LinkedIn fontSize="small" />}
                        label="LinkedIn" size="small" variant="outlined"
                        sx={{ color: '#0077b5', borderColor: '#0077b5', fontSize: '0.72rem', cursor: 'pointer' }}
                        onClick={() => window.open(p.linkedin.startsWith('http') ? p.linkedin : `https://${p.linkedin}`, '_blank')}
                    />
                )}
                {p.github && p.github !== 'not found' && (
                    <Chip
                        icon={<GitHub fontSize="small" />}
                        label="GitHub" size="small" variant="outlined"
                        sx={{ color: '#24292e', borderColor: '#c9d1d9', fontSize: '0.72rem', cursor: 'pointer' }}
                        onClick={() => window.open(p.github.startsWith('http') ? p.github : `https://${p.github}`, '_blank')}
                    />
                )}
            </Box>

            {p.summary && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1, display: 'block' }}>RÉSUMÉ</Typography>
                        <Typography variant="body2" sx={{ color: '#495057', lineHeight: 1.6 }}>{p.summary}</Typography>
                    </Box>
                    <Divider />
                </>
            )}
            {indScores && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1.5, display: 'block' }}>
                            SCORES PAR DIMENSION
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {indScores.semantic_score != null && <ScoreBar label="Sémantique" score={Math.round(indScores.semantic_score * 100)} />}
                            {indScores.core_skills_score != null && <ScoreBar label="Compétences" score={Math.round(indScores.core_skills_score * 100)} />}
                            {indScores.role_score != null && <ScoreBar label="Rôle" score={Math.round(indScores.role_score * 100)} />}
                            {indScores.language_score != null && <ScoreBar label="Langues" score={Math.round(indScores.language_score * 100)} />}
                            {indScores.seniority_score != null && <ScoreBar label="Séniorité" score={Math.round(indScores.seniority_score * 100)} />}
                            {indScores.evidence_score != null && <ScoreBar label="Preuves" score={Math.round(indScores.evidence_score * 100)} />}
                            {indScores.penalty_score != null && <ScoreBar label="Pénalité" score={Math.round(indScores.penalty_score * 100)} />}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            {(p.years_of_experience > 0 || p.education_level || p.field_of_studies) && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1, display: 'block' }}>FORMATION & EXPÉRIENCE</Typography>
                        {p.years_of_experience > 0 && (
                            <Typography variant="body2" sx={{ color: '#495057', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}><Schedule fontSize="small" /> {p.years_of_experience} ans d'expérience</Typography>
                        )}
                        {p.education_level && (
                            <Typography variant="body2" sx={{ color: '#495057', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}><School fontSize="small" /> {p.education_level}</Typography>
                        )}
                        {p.field_of_studies && (
                            <Typography variant="body2" sx={{ color: '#495057', display: 'flex', alignItems: 'center', gap: 1 }}><Library fontSize="small" /> {p.field_of_studies}</Typography>
                        )}
                    </Box>
                    <Divider />
                </>
            )}

            {p.work_experience && p.work_experience !== 'not found' && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1, display: 'block' }}>EXPÉRIENCE PROFESSIONNELLE</Typography>
                        <Typography variant="body2" sx={{ color: '#495057', lineHeight: 1.6 }}>{p.work_experience}</Typography>
                    </Box>
                    <Divider />
                </>
            )}

            {industries.length > 0 && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1.5, display: 'block' }}>SECTEURS D'ACTIVITÉ</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {industries.map((ind, i) => (
                                <Chip key={`${ind}-${i}`} label={ind} size="small" sx={{ backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            {rolesHeld.length > 0 && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1.5, display: 'block' }}>RÔLES</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {rolesHeld.map((role, i) => (
                                <Chip key={`${role}-${i}`} label={role} size="small" sx={{ backgroundColor: '#f1f5f9', color: '#475569', fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            {techSkills.length > 0 && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1.5, display: 'block' }}>COMPÉTENCES TECHNIQUES</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {techSkills.map((skill, i) => (
                                <Chip key={`${skill}-${i}`} label={skill} size="small" sx={{ backgroundColor: '#e0f2fe', color: '#0369a1', fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            {progLangs.length > 0 && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1.5, display: 'block' }}>LANGAGES DE PROGRAMMATION</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {progLangs.map((lang, i) => (
                                <Chip key={`${lang}-${i}`} label={lang} size="small" sx={{ backgroundColor: '#fae8ff', color: '#a21caf', fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            {seniorityProgLangs.length > 0 && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1.5, display: 'block' }}>SÉNIORITÉ PAR LANGAGE</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            {seniorityProgLangs.map((item, i) => (
                                <SeniorityBar key={`${item.language}-${i}`} label={item.language || item.name || String(i + 1)} level={item.level || ''} score={item.score || 0} />
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            {spokenLangs.length > 0 && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1.5, display: 'block' }}>LANGUES PARLÉES</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {spokenLangs.map((lang, i) => (
                                <Chip key={`${lang}-${i}`} label={lang} size="small" sx={{ backgroundColor: '#dcfce7', color: '#15803d', fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}
            {certifs.length > 0 && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1.5, display: 'block' }}>CERTIFICATIONS</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {certifs.map((c, i) => (
                                <Chip key={`${c}-${i}`} label={c} variant="outlined" size="small" sx={{ color: '#fd7e14', borderColor: '#ffe5cc', backgroundColor: '#fff8f5' }} />
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            {seniorityTech.length > 0 && (
                <>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1.5, display: 'block' }}>SÉNIORITÉ PAR TECHNOLOGIE</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            {seniorityTech.map((item, i) => (
                                <SeniorityBar key={`${item.technology}-${i}`} label={item.technology || item.tech || item.name || String(i + 1)} level={item.level || ''} score={item.score || 0} />
                            ))}
                        </Box>
                    </Box>
                    <Divider />
                </>
            )}

            {p.projects && p.projects !== 'not found' && p.projects !== 'No specific projects were detailed in the CV.' && (
                <Box>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#adb5bd', letterSpacing: 1, mb: 1, display: 'block' }}>PROJETS</Typography>
                    <Typography variant="body2" sx={{ color: '#495057', lineHeight: 1.6 }}>{p.projects}</Typography>
                </Box>
            )}

            {!indScores && techSkills.length === 0 && spokenLangs.length === 0 && rolesHeld.length === 0 && !p.summary && (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Aucun détail supplémentaire disponible pour ce candidat.
                </Alert>
            )}
        </Box>
    );
};

export default CandidatDetail;